import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { serviciosElegibles, serviciosExcluidos, entraEnDescuentoDeServicios, separarPorDecision } from '../utils/discountMatch';

interface Item { description?: string; name?: string; quantity?: number; unitPrice?: number; totalPrice?: number; optionCode?: string; qty?: number; sellingPrice?: number; }
type MainPaymentMethod = 'contado' | 'tc3' | 'tc6';
type CashPaymentCode = 'efectivo' | 'debito' | 'qr' | 'transferencia';

const formatCurrency = (n: number) => (Number(n||0)).toLocaleString('es-AR', { minimumFractionDigits: 0 });

const normalizeCode = (code: string = '') =>
  String(code).toLowerCase().replace(/[^a-z0-9]/g, '');

const canonicalPaymentCode = (code: string = '') => {
  const normalized = normalizeCode(code);
  if (['cash', 'contado', 'efectivo'].includes(normalized)) return 'efectivo';
  if (['debito', 'dbito', 'tarjetadebito', 'debit', 'td', 'tarjetad'].includes(normalized)) return 'debito';
  if (['qr', 'codigoqr', 'pagoqr', 'mercadopagoqr', 'transferenciaqr', 'mpqr'].includes(normalized)) return 'qr';
  if (['transferencia', 'transferenciabancaria', 'transfer'].includes(normalized)) return 'transferencia';
  if (['tc3', 'tc3p', 'tarjeta3', 'tarjeta3cuotas', '3cuotas'].includes(normalized)) return 'tc3';
  if (['tc6', 'tc6p', 'tarjeta6', 'tarjeta6cuotas', '6cuotas'].includes(normalized)) return 'tc6';
  return normalized;
};

const parseDiscountPaymentCodes = (discount: any): string[] =>
  Array.isArray(discount?.payment_method_codes)
    ? discount.payment_method_codes.map((code: string) => String(code))
    : [];

const parseSelectedPaymentCodes = (source: any): string[] => {
  if (!source) return [];
  if (Array.isArray(source)) return source.map(String);
  if (typeof source === 'string') {
    try {
      return parseSelectedPaymentCodes(JSON.parse(source));
    } catch {
      return [];
    }
  }
  if (typeof source === 'object') {
    if (Array.isArray(source.codes)) return source.codes.map(String);
    if (Array.isArray(source.methods)) {
      return source.methods
        .map((m: any) => m?.code)
        .filter(Boolean)
        .map(String);
    }
  }
  return [];
};

const cashMethodLabel = (code: string) => {
  switch (canonicalPaymentCode(code)) {
    case 'efectivo': return 'Efectivo';
    case 'debito': return 'Tarjeta de debito';
    case 'qr': return 'QR';
    case 'transferencia': return 'Transferencia';
    default: return 'Contado';
  }
};

const getPaymentMethodLabel = (method: string) => {
  switch (method) {
    case 'contado': return 'Contado';
    case 'efectivo': return 'Efectivo';
    case 'debito': return 'Tarjeta de debito';
    case 'qr': return 'QR';
    case 'transferencia': return 'Transferencia';
    case 'tc3': return 'TC 3P';
    case 'tc6': return 'TC 6';
    default: return 'Contado';
  }
};

const InteractiveBudgetView: React.FC = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [budget, setBudget] = useState<any>(null);
  // El link es de una versión que se reemplazó: { budgetNumber, latestNumber, latestToken }
  const [reemplazado, setReemplazado] = useState<{ budgetNumber?: string; latestNumber?: string | null; latestToken?: string | null } | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [company, setCompany] = useState<any>({});
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<MainPaymentMethod>('contado');
  const [selectedCashMethod, setSelectedCashMethod] = useState<'group' | CashPaymentCode>('group');
  const [accepting, setAccepting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('precio');
  const [rejectOther, setRejectOther] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    // Verificar si hay preferencia guardada
    const saved = localStorage.getItem('budget-theme');
    if (saved) return saved === 'dark';
    // Si no hay preferencia guardada, usar la del sistema
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Aplicar tema al documento
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('budget-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Escuchar cambios en la preferencia del sistema solo si no hay preferencia manual
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      const saved = localStorage.getItem('budget-theme');
      if (!saved) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  const rejectReasons = [
    { value: 'precio', label: 'Precio' },
    { value: 'lo_penso', label: 'Lo penso' },
    { value: 'no_volvio', label: 'No volvio' },
    { value: 'se_fue_a_otro', label: 'Se fue a otro' },
    { value: 'otro', label: 'Otro' }
  ];

  useEffect(() => {
    if (rejectReason !== 'otro' && rejectOther) {
      setRejectOther('');
    }
  }, [rejectOther, rejectReason]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const resp = await axios.get(`/api/budgets/public/by-token/${token}`);
        // Link de una versión vieja: se muestra el aviso con el acceso a la nueva
        if (resp.data?.superseded) {
          setReemplazado(resp.data);
          return;
        }
        setBudget(resp.data);
        const its: Item[] = Array.isArray(resp.data.items) ? resp.data.items : [];
        setItems(its);
        setCompany(resp.data.company || {});
        const codes = Array.from(new Set(its.filter(i=>!!(i as any).optionCode).map(i=>String((i as any).optionCode))));
        if (codes.length > 0) setSelectedOption(codes[0]);
      } catch (e: any) {
        setError(e.response?.data?.error || 'No se pudo cargar el presupuesto');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const getShownItems = (): Item[] => {
    const hasOptions = items.some(i=>!!(i as any).optionCode);
    if (hasOptions && selectedOption) {
      const base = items.filter(i=>!(i as any).optionCode);
      const chosen = items.filter(i=>String((i as any).optionCode)===String(selectedOption));
      return [...base, ...chosen];
    }
    return items;
  };

  const getItemTotal = (item: any) => {
    const qty = Number(item?.qty || item?.quantity || 1);
    const explicitTotal = Number(item?.totalPrice || 0);
    if (explicitTotal > 0) return explicitTotal;
    const originalPrice = Number(item?.originalPrice ?? 0);
    const unitPrice = Number(item?.sellingPrice ?? item?.unitPrice ?? item?.price ?? 0);
    const priceToUse = originalPrice > 0 ? originalPrice : unitPrice;
    return qty * priceToUse;
  };

  const isServiceItem = (item: any) => {
    const type = String(item?.type || '').toLowerCase();
    const category = String(item?.category || '').toLowerCase();
    return item?.isService === true || type === 'service' || type === 'servicio' || category === 'servicio';
  };

  const getDiscountBaseAmount = (discount: any, sourceItems: any[]) => {
    const appliesTo = String(discount?.applies_to || 'all');
    // Ítems libres (sin catálogo) solo si se tildó su casilla; los tildados
    // entran siempre (mismo criterio que el remito y el PDF)
    const { automaticos, tildados } = separarPorDecision(sourceItems);
    let porRegla = automaticos;
    if (appliesTo === 'services') {
      const elegibles = serviciosElegibles(discount);
      porRegla = automaticos.filter((item) => entraEnDescuentoDeServicios(item, elegibles, isServiceItem, serviciosExcluidos(discount)));
    } else if (appliesTo === 'products') {
      porRegla = automaticos.filter((item) => !isServiceItem(item));
    }
    return [...porRegla, ...tildados].reduce((acc, item) => acc + getItemTotal(item), 0);
  };

  const isDiscountApplicableForCode = (discount: any, paymentCode: string) => {
    if (!discount) return false;
    const scope = discount?.payment_scope === 'selected' ? 'selected' : 'all';
    if (scope !== 'selected') return true;

    const allowedCodes = parseDiscountPaymentCodes(discount).map((code) => canonicalPaymentCode(code));
    if (!allowedCodes.length) return false;
    const methodCanonical = canonicalPaymentCode(paymentCode);
    return allowedCodes.includes(methodCanonical);
  };

  const calculateDiscountForCode = (paymentCode: string) => {
    const discount = budget?.appliedDiscount;
    if (!discount || !isDiscountApplicableForCode(discount, paymentCode)) return 0;
    const shownItems = getShownItems();
    const baseAmount = getDiscountBaseAmount(discount, shownItems);
    if (baseAmount <= 0) return 0;

    if (String(discount.discount_type) === 'percentage') {
      let amount = (baseAmount * Number(discount.discount_value || 0)) / 100;
      if (discount.max_discount && amount > Number(discount.max_discount)) {
        amount = Number(discount.max_discount);
      }
      return Math.max(0, amount);
    }
    if (String(discount.discount_type) === 'fixed') {
      return Math.max(0, Number(discount.discount_value || 0));
    }
    return 0;
  };

  const calculateDiscountForMethod = (paymentMethod: MainPaymentMethod) => {
    if (paymentMethod === 'contado') {
      const cashCodes: CashPaymentCode[] = ['efectivo', 'debito', 'qr', 'transferencia'];
      const byCode = cashCodes.map((code) => calculateDiscountForCode(code));
      const hasAny = byCode.some((amount) => amount > 0);
      const hasAll = byCode.every((amount) => amount > 0);
      return hasAny && hasAll ? byCode[0] : 0;
    }
    return calculateDiscountForCode(paymentMethod);
  };

  const hasDiscount = !!budget?.appliedDiscount;
  const discountName = budget?.appliedDiscount?.name || 'Descuento';
  const subtotal = getShownItems().reduce((acc, item) => acc + getItemTotal(item as any), 0);
  const selectedPaymentCodes = useMemo(() => {
    const parsedCodes = parseSelectedPaymentCodes((budget as any)?.paymentMethodCodes || (budget as any)?.paymentOptions);
    if (parsedCodes.length) {
      return parsedCodes.map((code) => canonicalPaymentCode(code));
    }
    return ['efectivo', 'debito', 'qr', 'transferencia', 'tc3', 'tc6'];
  }, [budget]);
  const availableCashCodes = useMemo(() => {
    const cashCodes: CashPaymentCode[] = ['efectivo', 'debito', 'qr', 'transferencia'];
    const selectedSet = new Set(selectedPaymentCodes);
    return cashCodes.filter((code) => selectedSet.has(code));
  }, [selectedPaymentCodes]);
  const discountedCashOptions = useMemo(() => {
    return availableCashCodes
      .map((code) => {
        const discountAmount = Math.round(calculateDiscountForCode(code));
        return {
          code,
          discountAmount,
          price: Math.max(0, subtotal - discountAmount)
        };
      })
      .filter((option) => option.discountAmount > 0);
  }, [availableCashCodes, budget, items, selectedOption]);
  const regularCashCodes = useMemo(() => {
    const discounted = new Set(discountedCashOptions.map((option) => option.code));
    return availableCashCodes.filter((code) => !discounted.has(code));
  }, [availableCashCodes, discountedCashOptions]);
  const cashGroupLabel = useMemo(() => {
    if (!regularCashCodes.length) return 'Contado';
    const labels = regularCashCodes.map((code) => cashMethodLabel(code));
    if (labels.length === 1) return `Contado (${labels[0]})`;
    if (labels.length === 2) return `Contado (${labels[0]} o ${labels[1]})`;
    return `Contado (${labels.slice(0, -1).join(', ')} o ${labels[labels.length - 1]})`;
  }, [regularCashCodes]);
  const discountForSelectedMethod = selectedPaymentMethod === 'contado' && selectedCashMethod !== 'group'
    ? Math.round(calculateDiscountForCode(selectedCashMethod))
    : Math.round(calculateDiscountForMethod(selectedPaymentMethod));
  const total = Math.max(0, subtotal - discountForSelectedMethod);

  const cash = subtotal;
  const tc3 = Math.round(Math.max(0, subtotal - calculateDiscountForMethod('tc3')) * 1.15);
  const tc6 = Math.round(Math.max(0, subtotal - calculateDiscountForMethod('tc6')) * 1.24);
  const selectedPrice = selectedPaymentMethod === 'contado' && selectedCashMethod !== 'group'
    ? Math.max(0, subtotal - Math.round(calculateDiscountForCode(selectedCashMethod)))
    : selectedPaymentMethod === 'tc3'
      ? tc3
      : selectedPaymentMethod === 'tc6'
        ? tc6
        : cash;
  const selectedPaymentLabel = selectedPaymentMethod === 'contado'
    ? (selectedCashMethod === 'group' ? cashGroupLabel : cashMethodLabel(selectedCashMethod))
    : getPaymentMethodLabel(selectedPaymentMethod);

  useEffect(() => {
    const hasTc3 = selectedPaymentCodes.includes('tc3');
    const hasTc6 = selectedPaymentCodes.includes('tc6');
    if (selectedPaymentMethod === 'tc3' && !hasTc3) {
      setSelectedPaymentMethod('contado');
      return;
    }
    if (selectedPaymentMethod === 'tc6' && !hasTc6) {
      setSelectedPaymentMethod('contado');
    }
  }, [selectedPaymentMethod, selectedPaymentCodes]);

  useEffect(() => {
    if (selectedPaymentMethod !== 'contado') return;
    if (selectedCashMethod === 'group') {
      if (regularCashCodes.length === 0 && discountedCashOptions.length > 0) {
        setSelectedCashMethod(discountedCashOptions[0].code);
      }
      return;
    }
    const stillAvailable = discountedCashOptions.some((option) => option.code === selectedCashMethod);
    if (!stillAvailable) {
      setSelectedCashMethod(regularCashCodes.length > 0 ? 'group' : (discountedCashOptions[0]?.code || 'group'));
    }
  }, [selectedPaymentMethod, selectedCashMethod, regularCashCodes, discountedCashOptions]);

  const accept = async () => {
    if (!token) return;
    try {
      setAccepting(true);
      const response = await axios.post(`/api/budgets/public/${token}/accept`, { 
        selectedOption,
        paymentMethod: selectedPaymentMethod 
      });
      
      // Disparar evento para notificar al frontend principal
      window.dispatchEvent(new CustomEvent('new-notification'));
      
      // Actualizar el estado del presupuesto localmente
      if (response.data.budget) {
        setBudget(response.data.budget);
      }
      
      toast.success('¡Gracias! Aceptaste el presupuesto. Te contactaremos para coordinar.');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Error al aceptar presupuesto');
    } finally {
      setAccepting(false);
    }
  };

  const companyLogo = useMemo(() => {
    const raw = company?.logoDataURL || company?.companyLogoDataURL || company?.logo;
    if (!raw) return '';
    if (/^data:image|^https?:\/\//i.test(raw)) return raw;
    const base = (axios.defaults.baseURL || window.location.origin).replace(/\/$/, '');
    const normalized = raw.startsWith('/') ? raw : `/uploads/company/${raw}`;
    return `${base}${normalized}`;
  }, [company]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Cargando…</div>;
  if (error) return <div className="min-h-screen flex items-center justify-center text-red-600">{error}</div>;
  if (reemplazado) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 text-center shadow-lg">
          <h1 className="text-lg font-semibold text-foreground text-balance">Este presupuesto se actualizó</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {reemplazado.budgetNumber ? `El ${reemplazado.budgetNumber} ` : 'Este presupuesto '}
            tiene una versión nueva{reemplazado.latestNumber ? ` (${reemplazado.latestNumber})` : ''}. Revisá esa, que es la vigente.
          </p>
          {reemplazado.latestToken ? (
            <a
              href={`/presupuesto/${reemplazado.latestToken}`}
              className="mt-5 inline-flex w-full items-center justify-center rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Ver la versión nueva
            </a>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">Escribinos y te pasamos el presupuesto actualizado.</p>
          )}
        </div>
      </div>
    );
  }
  if (!budget) return null;

  // Verificar si el presupuesto ha vencido
  const isExpired = new Date(budget.validUntil) < new Date();
  if (isExpired && budget.status === 'pending') {
    // Actualizar el estado del presupuesto a vencido
    axios.post(`/api/budgets/public/${token}/expire`).catch(() => {});
  }

  const optionCodes = Array.from(new Set(items.filter(i=>!!(i as any).optionCode).map(i=>String((i as any).optionCode))));

  // Formatear número de WhatsApp para wa.me (quitar guiones y agregar código de país)
  const formatWhatsAppNumber = (phone: string) => {
    if (!phone) return '';
    // Quitar todos los caracteres que no sean dígitos
    const cleaned = phone.replace(/\D/g, '');
    if (!cleaned) return '';
    // Agregar código de país de Argentina si no está presente
    if (cleaned.startsWith('549')) return `+${cleaned}`;
    if (cleaned.startsWith('54')) return `+${cleaned}`;
    return `+549${cleaned}`;
  };

  // Obtener número de WhatsApp para mostrar
  const rawWhatsApp = company?.whatsapp || (company as any)?.whatsApp || '';
  // Formato display: quitar +549 o +54 del inicio para mostrar solo el número local
  const formatDisplayPhone = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('549')) return cleaned.slice(3);
    if (cleaned.startsWith('54')) return cleaned.slice(2);
    return cleaned;
  };
  const displayWhatsApp = rawWhatsApp ? formatDisplayPhone(rawWhatsApp) : '';
  
  // Usar WhatsApp de la empresa, o teléfono fijo como fallback (formateado para wa.me)
  const waPhone = formatWhatsAppNumber(displayWhatsApp);

  return (
    <>
      <div className="min-h-screen bg-background py-2 sm:py-8 px-2 sm:px-4 relative">
      <div className="max-w-3xl mx-auto bg-card border border-border rounded-lg shadow-lg p-3 sm:p-6">
        {/* Primer encabezado: Logo y datos de empresa */}
        <div className="flex items-start justify-between border-b border-border pb-4 mb-3 gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            {companyLogo ? (
              <img
                src={companyLogo}
                alt="Logo empresa"
                className="w-20 h-10 sm:w-[140px] sm:h-[60px] object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="h-8 w-16 bg-muted" />
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-sm sm:text-base font-semibold text-foreground">{company?.name || 'ROCAM'}</div>
            <div className="text-xs text-muted-foreground mt-1">{company?.address || ''}</div>
            <div className="text-xs text-muted-foreground mt-1">Tel: {company?.phone || ''}</div>
            {displayWhatsApp && (
              <div className="text-xs text-muted-foreground mt-1">WhatsApp: {displayWhatsApp}</div>
            )}
            {company?.instagram && (
              <div className="text-xs text-muted-foreground mt-1">Instagram: {company.instagram.startsWith('@') ? company.instagram : `@${company.instagram}`}</div>
            )}
            {(company?.weekdayHours || company?.saturdayHours) && (
              <div className="text-xs text-muted-foreground mt-1">
                {company.weekdayHours && `Lun a Vie: ${company.weekdayHours}`}
                {company.weekdayHours && company.saturdayHours && ' | '}
                {company.saturdayHours && `Sáb: ${company.saturdayHours}`}
              </div>
            )}
          </div>
        </div>

        {/* Segundo encabezado: Número de presupuesto y fecha */}
        <div className="flex items-center justify-between border-b border-border pb-3 mb-4">
          <h1 className="text-base sm:text-lg font-bold text-foreground">Presupuesto {budget.budgetNumber || ''}</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Válido hasta: {new Date(budget.validUntil).toLocaleDateString('es-AR')}</p>
        </div>

        {/* Estado del presupuesto */}
        {budget.status !== 'pending' && (
          <div className="mb-4 p-4 border border-border rounded-lg bg-muted/50">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-3 h-3 rounded-full flex-shrink-0 bg-primary"></span>
              <h3 className="text-base font-semibold text-foreground">
                Estado: {
                  budget.status === 'approved' ? 'Presupuesto Aceptado' :
                  budget.status === 'rejected' ? 'Presupuesto Rechazado' :
                  budget.status === 'expired' ? 'Presupuesto Vencido' :
                  'Presupuesto Pendiente'
                }
              </h3>
            </div>
            {budget.status === 'approved' && (
              <p className="text-sm text-foreground">
                ✅ El presupuesto fue aceptado exitosamente
              </p>
            )}
            {budget.status === 'rejected' && (
              <p className="text-sm text-foreground">
                ❌ El presupuesto fue rechazado
              </p>
            )}
            {budget.status === 'expired' && (
              <p className="text-sm text-muted-foreground">
                ⏰ El presupuesto ha vencido
              </p>
            )}
          </div>
        )}

        {/* Selector de opciones */}
        {optionCodes.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2 text-foreground">Elegí una opción</label>
            <div className="flex gap-2 flex-wrap">
              {optionCodes.map(code => {
                const active = selectedOption===code;
                return (
                  <button
                    key={code}
                    onClick={()=>setSelectedOption(code)}
                    className={`px-5 py-2.5 text-sm rounded-lg border-2 font-semibold transition-all ${
                      active
                        ? 'bg-primary text-primary-foreground border-primary shadow-md'
                        : 'bg-card text-foreground border-border hover:bg-accent hover:text-accent-foreground'
                    }`}
                  >
                    Opción {code}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-2">Las opciones no son acumulativas; el total final es Base + opción elegida.</p>
          </div>
        )}

        {/* Versión Desktop: Tabla */}
        <div className="hidden md:block">
          <table className="w-full border-collapse border border-border">
            <thead>
              <tr className="bg-muted">
                <th className="text-left p-3 border border-border text-foreground font-semibold">Descripción</th>
                <th className="text-center p-3 border border-border text-foreground font-semibold">Cant</th>
                <th className="text-right p-3 border border-border text-foreground font-semibold">Precio Unit.</th>
                <th className="text-right p-3 border border-border text-foreground font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {getShownItems().map((it, idx) => {
                const qty = Number((it as any).qty||it.quantity||1);
                const unit = Number((it as any).sellingPrice||it.unitPrice||0);
                const tot = Number((it as any).totalPrice) || (qty*unit);
                return (
                  <tr key={idx} className="border-b border-border">
                    <td className="p-3 border border-border text-foreground">{it.description || it.name}</td>
                    <td className="p-3 border border-border text-center text-foreground">{qty}</td>
                    <td className="p-3 border border-border text-right text-foreground">${formatCurrency(unit)}</td>
                    <td className="p-3 border border-border text-right font-medium text-foreground">${formatCurrency(tot)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              {hasDiscount && discountForSelectedMethod > 0 && (
                <tr className="bg-muted">
                  <td className="p-3 border border-border text-right font-medium text-foreground" colSpan={3}>
                    Descuento aplicado: {discountName}
                  </td>
                  <td className="p-3 border border-border text-right font-medium text-foreground">
                    -${formatCurrency(discountForSelectedMethod)}
                  </td>
                </tr>
              )}
              <tr className="bg-muted">
                <td className="p-3 border border-border text-right font-bold text-foreground" colSpan={3}>Total</td>
                <td className="p-3 border border-border text-right font-bold text-foreground text-lg">${formatCurrency(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Versión Móvil: Tarjetas */}
        <div className="md:hidden space-y-3">
          {getShownItems().map((it, idx) => {
            const qty = Number((it as any).qty||it.quantity||1);
            const unit = Number((it as any).sellingPrice||it.unitPrice||0);
            const tot = Number((it as any).totalPrice) || (qty*unit);
            return (
              <div key={idx} className="border border-border rounded-lg p-4 bg-card shadow-sm">
                <div className="font-medium text-foreground text-base mb-3">
                  {it.description || it.name}
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">Cantidad</div>
                    <div className="font-medium text-foreground">{qty}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">P. Unit.</div>
                    <div className="font-medium text-foreground">${formatCurrency(unit)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground text-xs mb-1">Total</div>
                    <div className="font-bold text-foreground">${formatCurrency(tot)}</div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Descuento y Total en móvil */}
          {hasDiscount && discountForSelectedMethod > 0 && (
            <div className="border border-border rounded-lg p-4 bg-muted">
              <div className="flex justify-between items-center">
                <div className="text-foreground font-medium">
                  Descuento aplicado: {discountName}
                </div>
                <div className="text-foreground font-bold text-lg">
                  -${formatCurrency(discountForSelectedMethod)}
                </div>
              </div>
            </div>
          )}

          <div className="border-2 border-border rounded-lg p-4 bg-muted">
            <div className="flex justify-between items-center">
              <div className="text-foreground font-bold text-lg">Total</div>
              <div className="text-foreground font-bold text-xl">${formatCurrency(total)}</div>
            </div>
          </div>
        </div>

        {/* Precios por medio de pago */}
        <div className="mt-6">
          <h3 className="text-base sm:text-lg font-semibold mb-1 text-foreground">Precios por medio de pago</h3>

          {/* Solo mostrar selector si el presupuesto está pendiente */}
          {budget.status === 'pending' && (
            <>
              <p className="text-sm text-muted-foreground mb-3">Seleccioná tu medio de pago preferido</p>
              {/* Selector de método de pago */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                {regularCashCodes.length > 0 && (
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedPaymentMethod === 'contado' && selectedCashMethod === 'group'
                        ? 'bg-foreground border-foreground shadow-lg ring-2 ring-foreground ring-offset-2'
                        : 'bg-card border-border hover:bg-accent hover:border-foreground/20'
                    }`}
                    onClick={() => {
                      setSelectedPaymentMethod('contado');
                      setSelectedCashMethod('group');
                    }}
                  >
                    <div className={`font-medium mb-1 ${selectedPaymentMethod === 'contado' && selectedCashMethod === 'group' ? 'text-background' : 'text-foreground'}`}>
                      {cashGroupLabel}
                    </div>
                    <div className={`font-bold text-lg ${selectedPaymentMethod === 'contado' && selectedCashMethod === 'group' ? 'text-background' : 'text-foreground'}`}>
                      ${formatCurrency(cash)}
                    </div>
                  </div>
                )}
                {discountedCashOptions.map((option) => {
                  const active = selectedPaymentMethod === 'contado' && selectedCashMethod === option.code;
                  return (
                    <div
                      key={option.code}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        active
                          ? 'bg-foreground border-foreground shadow-lg ring-2 ring-foreground ring-offset-2'
                          : 'bg-card border-border hover:bg-accent hover:border-foreground/20'
                      }`}
                      onClick={() => {
                        setSelectedPaymentMethod('contado');
                        setSelectedCashMethod(option.code);
                      }}
                    >
                      <div className={`font-medium mb-1 ${active ? 'text-background' : 'text-foreground'}`}>{cashMethodLabel(option.code)}</div>
                      <div className={`font-bold text-lg ${active ? 'text-background' : 'text-foreground'}`}>${formatCurrency(option.price)}</div>
                      <div className={`text-xs mt-1 ${active ? 'text-background/90' : 'text-muted-foreground'}`}>
                        Descuento aplicado: {discountName}
                      </div>
                    </div>
                  );
                })}
                {selectedPaymentCodes.includes('tc3') && (
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedPaymentMethod === 'tc3'
                        ? 'bg-foreground border-foreground shadow-lg ring-2 ring-foreground ring-offset-2'
                        : 'bg-card border-border hover:bg-accent hover:border-foreground/20'
                    }`}
                    onClick={() => {
                      setSelectedPaymentMethod('tc3');
                      setSelectedCashMethod('group');
                    }}
                  >
                    <div className={`font-medium mb-1 ${selectedPaymentMethod === 'tc3' ? 'text-background' : 'text-foreground'}`}>TC 3P</div>
                    <div className={`font-bold text-lg ${selectedPaymentMethod === 'tc3' ? 'text-background' : 'text-foreground'}`}>${formatCurrency(tc3)}</div>
                  </div>
                )}
                {selectedPaymentCodes.includes('tc6') && (
                  <div
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedPaymentMethod === 'tc6'
                        ? 'bg-foreground border-foreground shadow-lg ring-2 ring-foreground ring-offset-2'
                        : 'bg-card border-border hover:bg-accent hover:border-foreground/20'
                    }`}
                    onClick={() => {
                      setSelectedPaymentMethod('tc6');
                      setSelectedCashMethod('group');
                    }}
                  >
                    <div className={`font-medium mb-1 ${selectedPaymentMethod === 'tc6' ? 'text-background' : 'text-foreground'}`}>TC 6</div>
                    <div className={`font-bold text-lg ${selectedPaymentMethod === 'tc6' ? 'text-background' : 'text-foreground'}`}>${formatCurrency(tc6)}</div>
                  </div>
                )}
              </div>

              {/* Mostrar solo el método seleccionado */}
              {selectedPaymentMethod && (
                <div className="p-4 border-2 border-primary rounded-lg bg-primary/10">
                  <p className="text-sm text-foreground mb-2">
                    Medio de pago seleccionado: <strong className="text-primary">{selectedPaymentLabel}</strong>
                  </p>
                  <div className="text-lg font-bold text-foreground">
                    Precio final: ${formatCurrency(selectedPrice)}
                  </div>
                  {hasDiscount && discountForSelectedMethod > 0 && (
                    <div className="text-sm font-medium text-foreground mt-2">
                      Descuento aplicado: {discountName} (-${formatCurrency(discountForSelectedMethod)})
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Mostrar solo el método confirmado si ya no está pendiente */}
          {budget.status !== 'pending' && budget.paymentMethod && (
            <div className="p-4 border-2 border-primary rounded-lg bg-primary/10">
              <p className="text-sm text-foreground mb-2">
                Medio de pago confirmado: <strong className="text-primary">{budget.paymentMethod}</strong>
              </p>
              <div className="text-lg font-bold text-foreground">
                Precio final: ${formatCurrency(
                  budget.paymentMethod === 'Contado' ? cash :
                  budget.paymentMethod === 'TC 3P' ? tc3 :
                  budget.paymentMethod === 'TC 6P' ? tc6 : cash
                )}
              </div>
            </div>
          )}
        </div>

        {/* Acciones */}
        <div className="mt-6 flex flex-col gap-4">
          <div className="text-sm sm:text-base text-foreground text-center bg-muted border border-border rounded-lg p-3">
            Con esta compra sumás <strong className="text-foreground">{Math.floor(cash/1000)}</strong> puntos
          </div>

          <div className="flex flex-col gap-3">
            {/* Botones de contacto siempre disponibles */}
            <div className="grid grid-cols-2 gap-3">
              {waPhone && (
                <a
                  className="px-4 py-3 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg text-center text-sm sm:text-base font-medium transition-colors flex items-center justify-center gap-2"
                  href={`https://wa.me/${waPhone.replace('+', '')}?text=Hola!%20Sobre%20el%20presupuesto%20${encodeURIComponent(budget.budgetNumber||'')}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  WhatsApp
                </a>
              )}
              {company?.phone && (
                <a
                  className="px-4 py-3 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg text-center text-sm sm:text-base font-medium transition-colors"
                  href={`tel:${company.phone}`}
                >
                  Llamar
                </a>
              )}
            </div>

            {/* Botones de acción solo si el presupuesto está pendiente */}
            {budget.status === 'pending' ? (
              <div className="grid grid-cols-2 gap-3">
                <button
                  className="px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm sm:text-base font-medium transition-colors"
                  onClick={() => setShowRejectModal(true)}
                >
                  Rechazar
                </button>
                <button
                  disabled={accepting}
                  onClick={accept}
                  className="px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm sm:text-base font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Aceptar
                </button>
              </div>
            ) : (
              /* Mostrar mensaje según el estado */
              <div className="text-sm sm:text-base text-foreground p-4 text-center bg-muted border border-border rounded-lg font-medium">
                {budget.status === 'approved' && '✅ Presupuesto aceptado - Te contactaremos pronto'}
                {budget.status === 'rejected' && '❌ Presupuesto rechazado - Gracias por tu tiempo'}
                {budget.status === 'expired' && '⏰ Presupuesto vencido - Contactanos para un nuevo presupuesto'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Por que lo rechazaste?</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Select value={rejectReason} onValueChange={setRejectReason}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar motivo" />
              </SelectTrigger>
              <SelectContent>
                {rejectReasons.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {rejectReason === 'otro' && (
            <div className="space-y-2">
              <Textarea
                value={rejectOther}
                onChange={(event) => setRejectOther(event.target.value)}
                placeholder="Contanos el motivo (opcional)"
                rows={3}
              />
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              className="px-4 py-2 rounded-lg border text-sm font-medium"
              onClick={() => setShowRejectModal(false)}
              type="button"
            >
              Cancelar
            </button>
            <button
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium disabled:opacity-50"
              disabled={rejectSubmitting}
              onClick={async () => {
                try {
                  setRejectSubmitting(true);
                  const selected = rejectReasons.find((r) => r.value === rejectReason);
                  const detail = rejectReason === 'otro' ? rejectOther.trim() : (selected?.label || '');
                  await axios.post(`/api/budgets/public/${token}/reject`, {
                    reasonCode: rejectReason,
                    reasonText: detail
                  });

                  window.dispatchEvent(new CustomEvent('new-notification'));
                  setBudget((prev: any) => prev ? { ...prev, status: 'rejected' } : prev);
                  setShowRejectModal(false);
                  toast.success('Gracias. Tomamos tu respuesta.');
                } catch {
                  toast.error('No pudimos registrar la respuesta.');
                } finally {
                  setRejectSubmitting(false);
                }
              }}
              type="button"
            >
              Confirmar rechazo
            </button>
          </div>
        </div>
      </DialogContent>
      </Dialog>
    </>
  );
};

export default InteractiveBudgetView;
